import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Search, Boxes, Building2, Loader2, AlertCircle } from "lucide-react";
import { api } from "../lib/api";
import { statusEquipLabel, STATUS_EQUIP_BADGE, tipoClienteLabel } from "../lib/labels";

const MIN_QUERY_LEN = 2;
const DEBOUNCE_MS = 300;

/** Busca global Cmd/Ctrl+K — Sprint 10. */
export function GlobalSearch() {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");
  const [hi, setHi] = useState(0);
  const inputRef = useRef(null);
  const navigate = useNavigate();

  // Atalho Cmd/Ctrl+K
  useEffect(() => {
    const onKey = (e) => {
      const isMac = navigator.platform.toLowerCase().includes("mac");
      const trig = (isMac ? e.metaKey : e.ctrlKey) && e.key.toLowerCase() === "k";
      if (trig) {
        e.preventDefault();
        setOpen((o) => !o);
      }
      if (e.key === "Escape" && open) setOpen(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open]);

  useEffect(() => {
    if (open && inputRef.current) {
      setTimeout(() => inputRef.current?.focus(), 50);
    } else {
      setQ("");
      setItems([]);
      setHi(0);
      setErrorMsg("");
      setLoading(false);
    }
  }, [open]);

  // Debounced search (300ms, min 2 chars)
  useEffect(() => {
    const q_trim = q.trim();
    if (q_trim.length < MIN_QUERY_LEN) {
      setItems([]);
      setErrorMsg("");
      setLoading(false);
      return;
    }
    setLoading(true);
    setErrorMsg("");
    const t = setTimeout(async () => {
      try {
        const r = await api.get("/inventario/global-search", {
          params: { q: q_trim, limit: 12 },
        });
        const data = r.data?.items ?? r.data ?? [];
        setItems(Array.isArray(data) ? data : []);
      } catch (err) {
        // Bloco 3 fix: NÃO silenciar — registrar para diagnóstico
        // eslint-disable-next-line no-console
        console.error("[GlobalSearch] Busca falhou:", err?.response?.status, err?.response?.data || err?.message);
        const status = err?.response?.status;
        if (status === 401) setErrorMsg("Sessão expirada. Faça login novamente.");
        else if (status === 403) setErrorMsg("Sem permissão para a busca global.");
        else if (status === 429) setErrorMsg("Muitas requisições. Aguarde alguns segundos.");
        else if (!err?.response) setErrorMsg("Sem conexão com o servidor.");
        else setErrorMsg(err?.response?.data?.detail || "Erro ao buscar. Tente novamente.");
        setItems([]);
      } finally {
        setLoading(false);
      }
    }, DEBOUNCE_MS);
    return () => clearTimeout(t);
  }, [q]);

  const handleSelect = (it) => {
    setOpen(false);
    if (it.tipo === "equipamento") {
      navigate(`/app/inventario/equipamento/${it.id}`);
    } else {
      navigate(`/app/inventario/cliente/${it.id}`);
    }
  };

  const onKeyDown = (e) => {
    if (!items.length) return;
    if (e.key === "ArrowDown") { e.preventDefault(); setHi((h) => (h + 1) % items.length); }
    if (e.key === "ArrowUp") { e.preventDefault(); setHi((h) => (h - 1 + items.length) % items.length); }
    if (e.key === "Enter") { e.preventDefault(); handleSelect(items[hi]); }
  };

  if (!open) {
    return (
      <button
        onClick={() => setOpen(true)}
        className="flex items-center gap-2 px-3 py-1.5 rounded-md bg-zinc-100 hover:bg-zinc-200 text-xs text-zinc-600 transition border border-zinc-200"
        data-testid="global-search-trigger"
      >
        <Search className="w-3.5 h-3.5" />
        <span>Buscar equipamentos, clientes…</span>
        <kbd className="hidden md:inline-flex font-mono text-[10px] bg-white border border-zinc-200 px-1.5 py-0.5 rounded">⌘K</kbd>
      </button>
    );
  }

  const q_trim = q.trim();
  const showMinLenHint = q_trim.length > 0 && q_trim.length < MIN_QUERY_LEN;
  const showEmpty = q_trim.length >= MIN_QUERY_LEN && !loading && !errorMsg && items.length === 0;

  return (
    <div
      className="fixed inset-0 z-[100] bg-black/60 backdrop-blur-sm flex items-start justify-center pt-[12vh]"
      onClick={() => setOpen(false)}
      data-testid="global-search-overlay"
    >
      <div
        className="bg-white w-full max-w-2xl rounded-xl shadow-2xl border border-zinc-200 overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center gap-3 px-4 py-3 border-b border-zinc-100">
          <Search className="w-4 h-4 text-zinc-400" />
          <input
            ref={inputRef}
            value={q}
            onChange={(e) => { setQ(e.target.value); setHi(0); }}
            onKeyDown={onKeyDown}
            placeholder="Buscar por serial, placa, nome fantasia, CNPJ ou cidade…"
            className="flex-1 outline-none text-sm placeholder:text-zinc-400"
            data-testid="global-search-input"
            autoFocus
          />
          {loading && <Loader2 className="w-4 h-4 text-zinc-400 animate-spin" data-testid="global-search-loading" />}
          <kbd className="font-mono text-[10px] bg-zinc-100 px-1.5 py-0.5 rounded text-zinc-500">ESC</kbd>
        </div>
        <div className="max-h-[60vh] overflow-y-auto" data-testid="global-search-results">
          {!q && (
            <div className="px-6 py-12 text-center text-sm text-zinc-400">
              Digite ao menos {MIN_QUERY_LEN} caracteres para buscar entre clientes e equipamentos.
            </div>
          )}
          {showMinLenHint && (
            <div className="px-6 py-12 text-center text-sm text-zinc-400" data-testid="global-search-min-len">
              Digite mais {MIN_QUERY_LEN - q_trim.length} caractere{MIN_QUERY_LEN - q_trim.length > 1 ? "s" : ""} para buscar…
            </div>
          )}
          {errorMsg && (
            <div className="px-6 py-12 text-center text-sm text-red-600 flex flex-col items-center gap-2" data-testid="global-search-error">
              <AlertCircle className="w-6 h-6" />
              {errorMsg}
            </div>
          )}
          {showEmpty && (
            <div className="px-6 py-12 text-center text-sm text-zinc-400" data-testid="global-search-empty">
              Nenhum resultado para <strong className="text-zinc-600">"{q_trim}"</strong>.
            </div>
          )}
          {items.map((it, idx) => (
            <button
              key={`${it.tipo}-${it.id}`}
              onClick={() => handleSelect(it)}
              className={`w-full text-left px-4 py-3 flex items-center gap-3 hover:bg-zinc-50 border-b border-zinc-50 ${idx === hi ? "bg-zinc-50" : ""}`}
              data-testid={`gs-item-${it.tipo}-${it.id}`}
            >
              <div className={`p-2 rounded-md ${it.tipo === "equipamento" ? "bg-emerald-100 text-emerald-700" : "bg-blue-100 text-blue-700"}`}>
                {it.tipo === "equipamento" ? <Boxes className="w-4 h-4" /> : <Building2 className="w-4 h-4" />}
              </div>
              <div className="flex-1 min-w-0">
                {it.tipo === "equipamento" ? (
                  <>
                    <div className="font-medium text-sm text-zinc-900 truncate">
                      <span className="font-mono">{it.serial}</span>
                      {it.placa && <span className="text-zinc-400"> · placa {it.placa}</span>}
                    </div>
                    <div className="text-xs text-zinc-500 mt-0.5 truncate">
                      {it.cliente_atual ? `${it.cliente_atual} · ${it.cidade || "—"}/${it.uf || "—"}` : "Área neutra"}
                    </div>
                  </>
                ) : (
                  <>
                    <div className="font-medium text-sm text-zinc-900 truncate">{it.nome_fantasia}</div>
                    <div className="text-xs text-zinc-500 mt-0.5 truncate">
                      {it.cnpj || "—"} · {it.cidade || "—"}/{it.uf || "—"}
                    </div>
                  </>
                )}
              </div>
              {it.tipo === "equipamento" ? (
                <span className={`font-mono text-[10px] px-2 py-0.5 rounded ${STATUS_EQUIP_BADGE[it.status] || "bg-zinc-100"}`}>
                  {statusEquipLabel(it.status)}
                </span>
              ) : (
                <span className="font-mono text-[10px] px-2 py-0.5 rounded bg-blue-50 text-blue-700">
                  {tipoClienteLabel(it.tipo_cliente)}
                </span>
              )}
            </button>
          ))}
        </div>
        <div className="px-4 py-2 text-[10px] text-zinc-400 bg-zinc-50 border-t border-zinc-100 flex items-center gap-4">
          <span><kbd className="font-mono bg-white px-1 rounded border border-zinc-200">↑↓</kbd> navegar</span>
          <span><kbd className="font-mono bg-white px-1 rounded border border-zinc-200">⏎</kbd> abrir</span>
          <span><kbd className="font-mono bg-white px-1 rounded border border-zinc-200">ESC</kbd> fechar</span>
        </div>
      </div>
    </div>
  );
}
